import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import EmptyIcon from '@/assets/icons/empty-campaigns.svg';
import { ErrorStateFallback } from '@/components/ui/error-state';
import { EmptyState } from '@/components/ui/empty-state';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useNavigationPaths } from '@/hooks/use-navigation-paths';

import { Header } from './header';
import { JobPostingFlow } from './job-posting-flow';

export function JobPosting() {
  const intl = useIntl();
  const location = useLocation();
  const { handlePostJob, clearJobPostingStorage, initialSelectedOpeningIds } =
    useJobPostingContext();
  const { dashboardPath } = useNavigationPaths();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void handlePostJob();
  };

  // Clear storage when user navigates away from /post-job route. This won't trigger on page refresh since the component remounts with the same location
  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes('/post-job')) {
        clearJobPostingStorage();
      }
    };
  }, [location, clearJobPostingStorage]);

  if (initialSelectedOpeningIds.length === 0) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <EmptyState
          icon={EmptyIcon}
          header={intl.formatMessage({
            defaultMessage: 'No openings selected',
            description:
              'Job posting empty state heading: user arrived without selecting any openings',
          })}
          description={intl.formatMessage({
            defaultMessage:
              'Select at least one opening from the dashboard to start posting.',
            description:
              'Job posting empty state body: instructs user to go back to the dashboard and pick openings',
          })}
          buttonTitle={intl.formatMessage({
            defaultMessage: 'Go to dashboard',
            description:
              'CTA button on job posting empty state: navigates user back to the dashboard',
          })}
          link={dashboardPath}
        />
      </div>
    );
  }

  return (
    <ErrorStateFallback>
      <form onSubmit={handleSubmit}>
        <div className="flex h-screen flex-col gap-4">
          <Header />

          <JobPostingFlow />
        </div>
      </form>
    </ErrorStateFallback>
  );
}
